import { get } from 'lodash';
import React from 'react';
import moment from 'moment-timezone';
import { Production } from '__types__';
import { Shirt, TrashIcon } from 'lucide-react';

export type ProductionStatus = {
  id: number;
  name: string;
  status_type: string;
  colour: string;
};

/**
 * Get production status info from Redux store
 */
export function getStatusInfo(
  statusId: number,
  statuses: Record<number, ProductionStatus>
): ProductionStatus | null {
  return get(statuses, [statusId], null);
}

/**
 * Get badge variant and label for production status type
 */
export function getStatusBadgeVariant(statusType: string): {
  variant: 'info' | 'attention' | 'gray' | 'accent' | 'success' | 'danger';
  label: string;
} {
  switch (statusType) {
    case 'READY':
      return { variant: 'info', label: 'Ready' };
    case 'RESOURCING':
      return { variant: 'attention', label: 'Resourcing' };
    case 'OPEN':
      return { variant: 'gray', label: 'Open' };
    case 'ACTIVE':
      return { variant: 'accent', label: 'Active' };
    case 'COMPLETE':
      return { variant: 'success', label: 'Complete' };
    case 'ALERT':
      return { variant: 'danger', label: 'Alert' };
    default:
      return { variant: 'gray', label: statusType };
  }
}

/**
 * Get production icon component
 * Returns the icon from the production's icon field, or null if no icon
 */
export function getProductionIcon(
  production: Production
): React.ReactElement | null {
  if (!production.icon) {
    return React.createElement(
      Shirt,
      {
        className: 'text-base w-4 h-4'
      },
      production.icon
    );
  }
  return React.createElement(
    'span',
    {
      className: 'text-base'
    },
    production.icon
  );
}

/**
 * Format production date for display
 */
export function formatProductionDate(production: Production): string {
  return moment(production.production_date.start_date).format('MM/DD/YYYY');
}

/**
 * Get production subtitle/code for display
 */
export function getProductionSubtitle(
  code: Production['code']
): string | undefined {
  if (typeof code === 'object' && code?.nodes) {
    return code.nodes.map(n => n.id).join(' - ');
  }
  return code as string | undefined;
}
